import WMTSTileGrid from "ol/tilegrid/WMTS";
import proj4 from 'proj4';
import {get as getProjection, ProjectionLike, toLonLat} from "ol/proj";
import {fromCircle} from 'ol/geom/Polygon';
import {register} from 'ol/proj/proj4'
import {getArea, getLength} from 'ol/sphere';
import {Circle, LineString, MultiLineString, Polygon, SimpleGeometry, MultiPolygon} from "ol/geom";
import {formatUnits, UnitFormat, UnitType, degreesToRadians, radiansToDegrees, extendExtent} from "./general/function";
import {Extent, getWidth} from "ol/extent";
import {ProjectionConfiguration} from "./configuration/projection";
import olProjection from "ol/proj/Projection"
import {Measure} from "./general/callback";
import GIS from "./app"
import Geometry from "ol/geom/Geometry";

export type MeasureType = 'ground_radius' | 'area' | 'perimeter' | 'length';

type ProjectionMap = {
    [key: string]: olProjection;
}

type GridMap = {
    [key: string]: WMTSTileGrid;
}

type MeasureOptions = {
    projection?: ProjectionLike
    radius?: number
}

class Projection {
    private readonly GIS;
    private registered: ProjectionMap = {};
    public grid: GridMap = {}
    private readonly POLYGON_RADIUS_THRESHOLD_METERS = 20000;   // switch at >= 20 km
    private readonly POLYGON_LAT_THRESHOLD_DEGREES = 60;      // or |lat| >= 60°
    private readonly MIN_CIRCLE_SEGMENTS = 32;

    constructor(GIS: GIS) {
        this.GIS = GIS;
    }

    /**
     * Initialization of the Projection object.
     * @param configuration - Projection configuration
     */
    public init(configuration: ProjectionConfiguration) {
        configuration.coordinateSystem.map(system => {
            const epsgCode = "EPSG:" + system.epsg;
            proj4.defs(epsgCode, system.definition);
            register(proj4 as any);
            const olProjection = getProjection(epsgCode);
            if (olProjection !== null) {
                this.registered[system.epsg] = olProjection;
            } else {
                console && console.error("Error during initialization of the projection name " + system.epsg)
            }
        });

        if (configuration.matrixSet !== undefined) {
            configuration.matrixSet.map(matrix => {
                this.define(matrix.matrixSet, matrix.origin, matrix.size, matrix.levels, matrix.extent, matrix.tileSize, matrix.resolutions);
            })
        }
    }

    /**
     * Formats a given numeric value into a specific unit type and format.
     *
     * @param value - The numeric value to format.
     * @param type - The type of unit, either "area" or "length".
     * @param format - The format type, currently supports "label".
     * @returns The formatted value as a string with units if the format is "label", or the numeric value otherwise.
     *
     * @example
     * // Format an area value in square meters to hectares or square kilometers with a label.
     * GIS.Projection.formatUnits(15000000, 'area', 'label'); // "15 km²"
     *
     * @example
     * // Format a length value in meters to kilometers or meters with a label.
     * GIS.Projection.formatUnits(500, 'length', 'label'); // "500 m"
     */
    public formatUnits(value: number, type: UnitType, format: UnitFormat) {
        return formatUnits(value, type, format);
    }

    /**
     * Converts degrees to radians.
     *
     * @param degrees - The angle in degrees.
     * @returns The angle in radians.
     *
     * @example
     * // Convert 180 degrees to radians
     * GIS.Projection.degreesToRadians(180); // Math.PI
     */
    public degreesToRadians(degrees: number) {
        return degreesToRadians(degrees);
    }

    /**
     * Extends an extent value.
     * @param {array} extent1 - Extent array [minx, miny, maxx, maxy]
     * @param {array} extent2 - Extent array [minx, miny, maxx, maxy]
     * @returns {array} Extent array [minx, miny, maxx, maxy]
     */
    public extendExtent(extent1: Extent, extent2: Extent) {
        return extendExtent(extent1, extent2);
    }

    /**
     * Converts radians to degrees.
     *
     * @param radians - The angle in radians.
     * @returns The angle in degrees.
     *
     * @example
     * // Convert Math.PI radians to degrees
     * GIS.Projection.radiansToDegrees(Math.PI); // 180
     */
    public radiansToDegrees(radians: number) {
        return radiansToDegrees(radians);
    }

    /**
     * @param matrixSet - name of the matrixSet
     * @returns {@link https://openlayers.org/en/latest/apidoc/module-ol_tilegrid_WMTS-WMTSTileGrid.html}
     */
    public get(matrixSet: string) {
        return this.grid[matrixSet];
    }

    /**
     * Openlayers registered projection system
     * @param epsg - code of a coordinate system {@link https://epsg.io/}
     * @returns {@link https://openlayers.org/en/latest/apidoc/module-ol_proj_Projection-Projection.html}
     */
    public getRegistered(epsg: string | number) {
        if (this.registered[epsg]) {
            return this.registered[epsg];
        } else if (this.GIS.View.olObject.getProjection().getCode().replace('EPSG:', '') === epsg) {
            return this.GIS.View.olObject.getProjection();
        } else {
            throw new Error('EPSG code is not registered.');
        }
    }

    private calculateResolutions(levels: number, size: number) {
        let resolutions = new Array(levels);
        for (let z = 0; z < levels; ++z) {
            resolutions[z] = size / Math.pow(2, z);
        }
        return resolutions;
    }

    private define(matrixSet: string, origin: number[], matrixSize: number, levels: number, extent: import("ol/extent.js").Extent, tileSize: number | undefined, matrixResolutions: number[] | undefined) {
        let resolutions: number[] | undefined = matrixResolutions;
        const matrixIds = new Array(levels);
        let size = matrixSize;

        if (!size && extent) {
            size = getWidth(extent) / 256
        }

        if (!resolutions) {
            resolutions = this.calculateResolutions(levels, size);
        }

        for (let z = 0; z < levels; ++z) {
            matrixIds[z] = z;
        }

        this.grid[matrixSet] = new WMTSTileGrid({
            origin: origin,
            resolutions: resolutions,
            matrixIds: matrixIds,
            extent: extent,
            tileSize: tileSize
        });
    }

    private groundRadiusGeodesic(circle: Circle, options: any) {
        const center = circle.getCenter();
        const radius = circle.getRadius();
        const edge = [center[0] + radius, center[1]];
        return getLength(new LineString([center, edge]), options);
    }

    private circleAreaPerimeterAnalytic(groundRadius: number, R = 6378137) {
        const alpha = groundRadius / R; // radians
        const area = 2 * Math.PI * R * R * (1 - Math.cos(alpha));
        const perimeter = 2 * Math.PI * R * Math.sin(alpha);
        return {area, perimeter};
    }

    /**
     * Number of segments needed to approximate a circle so that every chord is
     * at most {@link targetChordLength} projection units long (never fewer than
     * {@link MIN_CIRCLE_SEGMENTS}). This is the single source of truth for how
     * finely a circle is densified, so measurement and geometry conversion stay
     * in agreement.
     */
    private circleSegmentCount(circle: Circle, targetChordLength: number) {
        const radiusUnits = circle.getRadius(); // in projection units
        const circumferenceUnits = 2 * Math.PI * radiusUnits;

        return Math.max(
            this.MIN_CIRCLE_SEGMENTS,
            Math.ceil(circumferenceUnits / targetChordLength)
        );
    }

    /**
     * Chord length (in projection units) used to densify a circle, chosen from
     * its ground radius. Larger circles tolerate longer chords because the
     * relative error stays negligible. Shared by measurement and conversion so
     * both produce the same polygon.
     */
    private circleChordLength(groundRadius: number) {
        return groundRadius >= 50000 ? 500 : 200;
    }

    private circleAreaPerimeterPolygonized(
        circle: Circle,
        options: any,
        targetChordLength = 200 // length of each chord in *projection units*
    ) {
        const segmentCount = this.circleSegmentCount(circle, targetChordLength);

        const circlePolygon = fromCircle(circle, segmentCount);

        const area = Math.abs(getArea(circlePolygon, options));

        const outerRing = circlePolygon.getLinearRing(0);
        if (!outerRing) {
            const coords = (circlePolygon.getCoordinates()[0] ?? []) as number[][];
            const perimeter = coords.length > 1 ? getLength(new LineString(coords), options) : 0;
            return {area, perimeter};
        }

        const perimeter = getLength(new LineString(outerRing.getCoordinates()), options);
        return {area, perimeter};
    }

    private shouldPolygonizeCircle(circle: Circle, options: any) {
        const groundRadius = this.groundRadiusGeodesic(circle, options); // meters
        let latAbsolute = 0;
        try {
            const [, lat] = toLonLat(circle.getCenter(), options.projection as any);
            latAbsolute = Math.abs(lat);
        } catch {
            console.warn('shouldPolygonizeCircle: Unable to convert circle center to lat/lon coordinates.');
        }
        const usePolygon = groundRadius >= this.POLYGON_RADIUS_THRESHOLD_METERS || latAbsolute >= this.POLYGON_LAT_THRESHOLD_DEGREES;
        return {usePolygon, gr: groundRadius, latAbs: latAbsolute};
    }

    /**
     * Clear registered projections and grids
     */
    public clear() {
        this.registered = {};
        this.grid = {};
    }

    /**
     * Measure length, perimeter, ground_radius or area
     * @param geometry - {@link https://openlayers.org/en/latest/apidoc/module-ol_geom_SimpleGeometry-SimpleGeometry.html}
     * @param type - length, area, ground_radius or perimeter
     * @param epsg - code of a coordinate system {@link https://epsg.io/}
     * @param callback
     * @returns calculated length, area, ground_radius or perimeter
     */
    public measure(
        geometry: Geometry | Circle | SimpleGeometry | LineString | Polygon | MultiPolygon | MultiLineString,
        type: MeasureType,
        epsg?: string,
        callback?: Measure
    ) {
        const options: MeasureOptions = {};
        options.projection = epsg ? this.getRegistered(epsg) : this.GIS.View.olObject.getProjection();
        options.radius = 6378137;

        const finish = (v: number) => {
            if (callback) callback(v);
            return v;
        };

        switch (type) {
            case 'ground_radius': {
                if (geometry instanceof Circle) {
                    const groundRadius = this.groundRadiusGeodesic(geometry, options);
                    return finish(groundRadius);
                }

                if (geometry instanceof Polygon) {
                    const extent = geometry.getExtent();

                    const center = [
                        (extent[0] + extent[2]) / 2,
                        (extent[1] + extent[3]) / 2
                    ];

                    const ring = geometry.getCoordinates()[0];
                    if (ring && ring.length > 0) {
                        const tempLine = new LineString([center, ring[0]]);
                        const dist = getLength(tempLine, options);
                        return finish(dist);
                    }
                }

                return finish(0);
            }
            case 'area': {
                if (geometry instanceof Circle) {
                    const {usePolygon, gr} = this.shouldPolygonizeCircle(geometry, options);
                    if (usePolygon) {
                        const segLen = this.circleChordLength(gr); // meters
                        const {area} = this.circleAreaPerimeterPolygonized(geometry, options, segLen);
                        return finish(area);
                    } else {
                        const {area} = this.circleAreaPerimeterAnalytic(gr, options.radius!);
                        return finish(area);
                    }
                }
                const area = Math.abs(getArea(geometry as any, options));
                return finish(area);
            }

            case 'perimeter': {
                if (geometry instanceof Circle) {
                    const {usePolygon, gr} = this.shouldPolygonizeCircle(geometry, options);
                    if (usePolygon) {
                        const segmentLength = this.circleChordLength(gr); // meters
                        const {perimeter} = this.circleAreaPerimeterPolygonized(geometry, options, segmentLength);
                        return finish(perimeter);
                    } else {
                        const {perimeter} = this.circleAreaPerimeterAnalytic(gr, options.radius!);
                        return finish(perimeter);
                    }
                }
                let perimeter = 0;
                if (geometry instanceof Polygon) {
                    const rings = geometry.getLinearRings();
                    for (const r of rings) perimeter += getLength(new LineString(r.getCoordinates()), options);
                } else if (geometry instanceof MultiPolygon) {
                    for (const poly of geometry.getPolygons()) {
                        for (const r of poly.getLinearRings()) {
                            perimeter += getLength(new LineString(r.getCoordinates()), options);
                        }
                    }
                } else if (geometry instanceof LineString || geometry instanceof MultiLineString) {
                    perimeter += getLength(geometry as any, options);
                }

                return finish(perimeter);
            }

            case 'length': {
                const length = getLength(geometry as any, options);
                return finish(length);
            }
        }
    }

    /**
     * Convert a {@link Circle} into a {@link Polygon}, densified to the same
     * resolution used by {@link measure}. OpenLayers' `fromCircle` defaults to
     * only 32 sides, which underestimates the circle area by up to ~0.6 % and
     * produced the header/overlay discrepancy for large circles. Routing every
     * circle-to-polygon conversion through this method keeps area, perimeter
     * and geometry calculations consistent across the application.
     *
     * @param circle - the circle geometry to convert
     * @param epsg - optional coordinate system code; defaults to the view projection
     * @returns a densified polygon approximating the circle
     * @example GIS.Projection.circleToPolygon(circleGeometry);
     */
    public circleToPolygon(circle: Circle, epsg?: string): Polygon {
        const options: MeasureOptions = {};
        options.projection = epsg ? this.getRegistered(epsg) : this.GIS.View.olObject.getProjection();
        options.radius = 6378137;

        const groundRadius = this.groundRadiusGeodesic(circle, options);
        const targetChordLength = this.circleChordLength(groundRadius);
        const segmentCount = this.circleSegmentCount(circle, targetChordLength);

        return fromCircle(circle, segmentCount);
    }
}

export default Projection;